import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { trackDashboardLoad, trackDashboardFilterChange } from '../utils/appInsights';

/**
 * Hook to automatically track dashboard load and filter changes
 * 
 * @param dashboardName - Name of the dashboard (e.g., "Incident", "ServiceRequest")
 * @param filters - Object containing all filter state values
 * @param options - Optional configuration
 */
export function useDashboardTracking(
  dashboardName: string,
  filters: { [key: string]: string | number | boolean | string[] | undefined },
  options?: {
    trackOnMount?: boolean; // Track load event on mount (default: true)
    debounceMs?: number; // Debounce filter change tracking (default: 500ms)
  }
): void {
  const { user } = useAuth();
  const previousFiltersRef = useRef<{ [key: string]: any }>({});
  const hasTrackedLoadRef = useRef(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialMountRef = useRef(true);

  const { trackOnMount = true, debounceMs = 500 } = options || {};

  // Get user email for logging (full name is derived in appInsights via userRoleInfo)
  const userName = user?.email || user?.firstName || user?.lastName || 'Unknown';

  // Extract user role and permission information
  const userRoleInfo = user ? {
    firstName: user.firstName,
    lastName: user.lastName,
    role: user.role,
    roleType: user.roleType,
    roleLevel: user.roleLevel,
    userClass: user.class,
    customerId: user.customerId,
    customerName: user.customerName,
    engagementId: user.engagementId,
    engagementName: user.engagementName,
    isServiceDeskUser: user.isServiceDeskUser,
  } : undefined;

  // Track dashboard load on mount (after filters are available)
  useEffect(() => {
    if (trackOnMount && !hasTrackedLoadRef.current && user) {
      // Extract only applied filters (non-empty values)
      const appliedFilters: { [key: string]: any } = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              appliedFilters[key] = value;
            }
          } else {
            appliedFilters[key] = value;
          }
        }
      });

      trackDashboardLoad(dashboardName, userName, appliedFilters, userRoleInfo);
      hasTrackedLoadRef.current = true;
    }
    // Only depend on user and dashboardName - filters will be captured on first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dashboardName, trackOnMount]);

  // Track filter changes
  useEffect(() => {
    // Skip on initial mount to avoid logging default values
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      // Store initial filter values
      previousFiltersRef.current = { ...filters };
      return;
    }

    if (!user) {
      return;
    }

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce filter change tracking to avoid excessive logs
    debounceTimerRef.current = setTimeout(() => {
      // Detect which filters have changed
      const changedFilters: { [key: string]: any } = {};
      let hasChanges = false;

      // Check for new or changed filters
      Object.entries(filters).forEach(([key, currentValue]) => {
        const previousValue = previousFiltersRef.current[key];

        // Check if value has actually changed
        const hasChanged = 
          JSON.stringify(currentValue) !== JSON.stringify(previousValue);

        if (hasChanged) {
          // Only include if current value is meaningful (not empty)
          if (
            currentValue !== undefined &&
            currentValue !== null &&
            currentValue !== ''
          ) {
            if (Array.isArray(currentValue)) {
              if (currentValue.length > 0) {
                changedFilters[key] = currentValue;
                hasChanges = true;
              }
            } else {
              changedFilters[key] = currentValue;
              hasChanges = true;
            }
          } else if (
            previousValue !== undefined &&
            previousValue !== null &&
            previousValue !== ''
          ) {
            // Filter was cleared - include it to track the removal
            changedFilters[key] = '';
            hasChanges = true;
          }
        }
      });

      // Check for removed filters (present in previous but not in current)
      Object.keys(previousFiltersRef.current).forEach((key) => {
        if (!(key in filters)) {
          changedFilters[key] = '';
          hasChanges = true;
        }
      });

      // Only track if there are actual changes
      if (hasChanges) {
        // Get all currently applied filters (not just changed ones)
        const appliedFilters: { [key: string]: any } = {};
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              if (value.length > 0) {
                appliedFilters[key] = value;
              }
            } else {
              appliedFilters[key] = value;
            }
          }
        });

        trackDashboardFilterChange(dashboardName, userName, appliedFilters, userRoleInfo);
      }

      // Update previous filters for next comparison
      previousFiltersRef.current = { ...filters };
    }, debounceMs);
  }, [filters, dashboardName, userName, user, userRoleInfo, debounceMs]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);
}

